import { useEffect } from 'react';

const VIDEO_URL =
  'https://static.readdy.ai/video/330c3edc4de2fd8d5bdc73e76ede45e4/9b3b60fac0662ba8e2130cc5be9a34fb.mp4';
const POSTER_URL =
  'https://static.readdy.ai/image/7b095d95fe974d6d04490d0833e89a3e/60a3015d78069aed18e5740b58bc65ee.jpeg';
const DURATION = 14;

type Beat = {
  el: HTMLElement;
  from: number;
  to: number;
  parts: HTMLElement[];
  lines: HTMLElement[];
  chips: HTMLElement[];
};

type Chapter = {
  el: HTMLElement;
  stage: HTMLElement;
  beats: Beat[];
  src: string;
  dur: number;
  video: HTMLVideoElement | null;
  playing: boolean;
  held: boolean;
  wait: boolean;
  clock: number;
  armed?: boolean;
};

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const ease = (x: number) => 1 - Math.pow(1 - x, 3);

function splitHeadlines(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('.beat :is(h1, h2)[data-split="lines"]').forEach((h) => {
    const lines = h.innerHTML
      .split(/<br\s*\/?>/i)
      .map((s) => s.trim())
      .filter(Boolean);
    h.innerHTML = lines
      .map((l) => `<span class="line"><span class="line-in">${l}</span></span>`)
      .join('');
  });
}

function soften(el: Element) {
  const target = el as HTMLImageElement | HTMLVideoElement;
  const nw = target.naturalWidth || (target as HTMLVideoElement).videoWidth;
  const nh = target.naturalHeight || (target as HTMLVideoElement).videoHeight;
  if (!nw || !nh) return;
  const r = target.getBoundingClientRect();
  if (!r.width || !r.height) return;
  const factor = Math.max(r.width / nw, r.height / nh);
  const px = factor <= 1.15 ? 0 : Math.min(1.6, Math.max(0.4, (factor - 1) * 1.2));
  target.style.setProperty('--soften', `${px.toFixed(2)}px`);
}

export default function HeroFilm() {
  useEffect(() => {
    const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const FINE = window.matchMedia('(pointer: fine)').matches;

    const FADE = 0.1;
    const STAGGER = 0.035;
    const NEAR = '200px';
    const FAR = '260%';

    const chapterEls = Array.from(document.querySelectorAll<HTMLElement>('.chapter'));
    if (!chapterEls.length) return undefined;

    if (REDUCED) {
      chapterEls.forEach((el) => el.classList.add('is-static'));
      return undefined;
    }

    splitHeadlines(document.body);

    const chapters: Chapter[] = chapterEls.map((el) => {
      const beats: Beat[] = Array.from(el.querySelectorAll<HTMLElement>('.beat')).map((b) => ({
        el: b,
        from: parseFloat(b.dataset.from || '0') || 0,
        to: b.dataset.to ? parseFloat(b.dataset.to) : 1,
        parts: Array.from(b.children).filter(
          (c): c is HTMLElement => c instanceof HTMLElement && !c.classList.contains('chips'),
        ),
        lines: Array.from(b.querySelectorAll<HTMLElement>('.line-in')),
        chips: Array.from(b.querySelectorAll<HTMLElement>('.chips li')),
      }));
      return {
        el,
        stage: el.querySelector<HTMLElement>('.stage') as HTMLElement,
        beats,
        src: el.dataset.video || '',
        dur: parseFloat(el.dataset.duration || '') || beats.length * 5,
        video: null,
        playing: false,
        held: false,
        wait: !!el.dataset.video,
        clock: 0,
      };
    });

    const afterLCP = new Promise<void>((res) => {
      const img = document.querySelector<HTMLImageElement>('.chapter .plate');
      if (!img || img.complete) {
        res();
        return;
      }
      img.addEventListener('load', () => res(), { once: true });
      img.addEventListener('error', () => res(), { once: true });
      setTimeout(res, 800);
    });

    function posterOf(c: Chapter) {
      const plate = c.stage.querySelector('.plate');
      if (plate && plate.getAttribute('src')) return plate.getAttribute('src') as string;
      return c.el.dataset.poster || '';
    }

    function start(c: Chapter) {
      const v = c.video;
      if (!v) return;
      const p = v.play();
      if (!p || typeof p.then !== 'function') {
        c.playing = true;
        return;
      }
      p.then(() => {
        c.playing = true;
        if (c.wait) {
          c.wait = false;
        } else if (Number.isFinite(v.duration) && v.duration > 0 && Math.abs(c.clock * v.duration - v.currentTime) > 1.5) {
          v.currentTime = c.clock * v.duration;
        }
        if (c.held) v.pause();
      }).catch(() => {
        c.playing = false;
        c.wait = false;
        if (c.armed) return;
        c.armed = true;
        const retry = () => start(c);
        window.addEventListener('pointerdown', retry, { once: true, passive: true });
        window.addEventListener('touchstart', retry, { once: true, passive: true });
      });
    }

    async function attach(c: Chapter | undefined) {
      if (!c || c.video) return;
      await afterLCP;
      if (c.video || !c.el.isConnected) return;
      const v = document.createElement('video');
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.loop = true;
      v.preload = 'auto';
      v.disablePictureInPicture = true;
      v.tabIndex = -1;
      v.setAttribute('aria-hidden', 'true');
      const poster = posterOf(c);
      if (poster) v.poster = poster;
      v.src = c.src;
      window.setTimeout(() => {
        c.wait = false;
      }, 5000);
      v.addEventListener(
        'loadedmetadata',
        () => {
          if (Number.isFinite(v.duration) && v.duration > 0) c.dur = v.duration;
          soften(v);
        },
        { once: true },
      );
      v.addEventListener('loadeddata', () => c.stage.classList.add('is-live'), { once: true });
      const veil = c.stage.querySelector('.veil');
      c.stage.insertBefore(v, veil);
      c.video = v;
      start(c);
    }

    function detach(c: Chapter | undefined) {
      if (!c || !c.video) return;
      c.stage.classList.remove('is-live');
      c.playing = false;
      c.video.pause();
      c.video.removeAttribute('src');
      c.video.load();
      c.video.remove();
      c.video = null;
    }

    function hold(c: Chapter, on: boolean) {
      if (c.held === on) return;
      c.held = on;
      const v = c.video;
      if (!v || !c.playing) return;
      if (on) v.pause();
      else v.play().catch(() => {});
    }

    function setState(el: HTMLElement, o: number, leaving: boolean) {
      el.style.setProperty('--o', o.toFixed(3));
      el.style.setProperty('--o-inv', (1 - o).toFixed(3));
      el.style.setProperty('--dir', leaving ? '1' : '-1');
    }

    function drive(el: HTMLElement, local: number, i: number, n: number) {
      const tIn = clamp01((local - i * STAGGER) / FADE);
      const tOut = clamp01((1 - local - (n - 1 - i) * STAGGER) / FADE);
      setState(el, ease(Math.min(tIn, tOut)), tOut < 1);
    }

    function applyBeats(c: Chapter, p: number) {
      c.beats.forEach((b) => {
        const span = b.to - b.from;
        const local = span > 0 ? (p - b.from) / span : 0;
        const inside = local >= 0 && local < 1;
        b.el.style.setProperty('--local', clamp01(local).toFixed(3));
        if (!inside) {
          [...b.parts, ...b.lines, ...b.chips].forEach((el) => setState(el, 0, local > 1));
        } else {
          b.parts.forEach((el, i) => drive(el, local, i, b.parts.length));
          b.lines.forEach((el, i) => drive(el, local, i * 0.8, b.lines.length * 0.8));
          b.chips.forEach((el, i) => drive(el, local, 2 + i, 2 + b.chips.length));
        }
        b.el.classList.toggle('is-on', inside);
        b.el.classList.toggle('is-leaving', inside && 1 - local < FADE + STAGGER * 4);
      });
    }

    function playhead(c: Chapter, dt: number) {
      const v = c.video;
      if (c.playing && v && Number.isFinite(v.duration) && v.duration > 0) {
        c.clock = clamp01(v.currentTime / v.duration);
        return c.clock;
      }
      if (!c.held && !c.wait) c.clock = (c.clock + dt / Math.max(1, c.dur)) % 1;
      return c.clock;
    }

    function setExit(c: Chapter) {
      const top = c.el.getBoundingClientRect().top;
      c.el.style.setProperty('--exit', clamp01(-top / Math.max(1, window.innerHeight)).toFixed(3));
    }

    const stages: HTMLElement[] = [];
    const pointerOver = (e: Event) => {
      const c = chapters.find((ch) => ch.stage === e.currentTarget);
      const t = e.target as HTMLElement;
      if (c && t.closest('.beat')) hold(c, true);
    };
    const pointerOut = (e: Event) => {
      const c = chapters.find((ch) => ch.stage === e.currentTarget);
      const rel = (e as PointerEvent).relatedTarget as HTMLElement | null;
      if (c && (!rel || !rel.closest || !rel.closest('.beat'))) hold(c, false);
    };

    if (FINE) {
      chapters.forEach((c) => {
        stages.push(c.stage);
        c.stage.addEventListener('pointerover', pointerOver);
        c.stage.addEventListener('pointerout', pointerOut);
      });
    }

    const onFirstScroll = () => {
      document.documentElement.style.setProperty('--cue', '0');
    };
    window.addEventListener('scroll', onFirstScroll, { once: true, passive: true });

    let raf = 0;
    let prev = 0;
    const inView = new Set<HTMLElement>();

    function tick(now: number) {
      const dt = prev ? Math.min(0.1, (now - prev) / 1000) : 0;
      prev = now;
      chapters.forEach((c) => {
        setExit(c);
        applyBeats(c, playhead(c, dt));
      });
      raf = window.requestAnimationFrame(tick);
    }

    function runRaf(on: boolean) {
      if (on && !raf) {
        prev = 0;
        raf = window.requestAnimationFrame(tick);
      }
      if (!on && raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    }

    const near = new IntersectionObserver(
      (es) => es.forEach((e) => {
        if (e.isIntersecting) attach(chapters.find((c) => c.el === e.target));
      }),
      { rootMargin: NEAR },
    );
    const far = new IntersectionObserver(
      (es) => es.forEach((e) => {
        if (!e.isIntersecting) detach(chapters.find((c) => c.el === e.target));
      }),
      { rootMargin: FAR },
    );
    const run = new IntersectionObserver(
      (es) => es.forEach((e) => {
        const target = e.target as HTMLElement;
        if (e.isIntersecting) inView.add(target);
        else {
          inView.delete(target);
          const c = chapters.find((ch) => ch.el === target);
          if (c) setExit(c);
        }
        runRaf(inView.size > 0);
      }),
    );

    chapters.forEach((c) => {
      near.observe(c.el);
      far.observe(c.el);
      run.observe(c.el);
    });

    // POINTER PARALLAX — the front planes follow the mouse.
    let removePointer = () => {};
    if (FINE) {
      const root = document.documentElement;
      const MLERP = 0.08;
      let tx = 0;
      let ty = 0;
      let mx = 0;
      let my = 0;
      let mraf = 0;
      const settle = () => {
        mx += (tx - mx) * MLERP;
        my += (ty - my) * MLERP;
        root.style.setProperty('--mx', mx.toFixed(3));
        root.style.setProperty('--my', my.toFixed(3));
        mraf = Math.abs(tx - mx) + Math.abs(ty - my) > 0.002 ? window.requestAnimationFrame(settle) : 0;
      };
      const aim = (x: number, y: number) => {
        tx = x;
        ty = y;
        if (!mraf) mraf = window.requestAnimationFrame(settle);
      };
      const onMove = (e: PointerEvent) => {
        aim((e.clientX / window.innerWidth) * 2 - 1, (e.clientY / window.innerHeight) * 2 - 1);
      };
      const onLeave = () => aim(0, 0);
      window.addEventListener('pointermove', onMove, { passive: true });
      document.addEventListener('mouseleave', onLeave);
      removePointer = () => {
        window.removeEventListener('pointermove', onMove);
        document.removeEventListener('mouseleave', onLeave);
        if (mraf) window.cancelAnimationFrame(mraf);
      };
    }

    // ---- panels: reveal, parallax, manifesto, smoothing ----
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }),
      { rootMargin: '0px 0px -12% 0px' },
    );
    document.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
      const parent = el.parentElement;
      if (parent) {
        const sibs = Array.from(parent.children).filter((c) => c.hasAttribute('data-reveal'));
        el.style.setProperty('--i', String(sibs.indexOf(el)));
      }
      io.observe(el);
    });

    const layers = Array.from(document.querySelectorAll<HTMLElement>('[data-parallax]'));
    let praf = 0;
    const paint = () => {
      praf = 0;
      const h = window.innerHeight;
      layers.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -h || r.top > 2 * h) return;
        const centre = (r.top + r.height / 2 - h / 2) / h;
        let speed = parseFloat(el.dataset.parallax || '.32');
        if (el.classList.contains('panel-bg') && window.innerWidth < 780) speed = Math.min(speed, 0.22);
        el.style.setProperty('--py', `${(centre * -speed * h).toFixed(1)}px`);
      });
    };
    const onScrollPaint = () => {
      if (!praf) praf = window.requestAnimationFrame(paint);
    };

    const SOFT = '.stage video, .plate, .panel-bg';
    const softenAll = () => document.querySelectorAll(SOFT).forEach(soften);
    const onSoftLoad = (e: Event) => {
      const t = e.target as HTMLElement;
      if (t.matches && t.matches(SOFT)) soften(t);
    };

    const manifestos = Array.from(document.querySelectorAll<HTMLElement>('[data-words]')).map((el) => {
      const wrap = (node: Node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === 3) {
            const frag = document.createDocumentFragment();
            (child.textContent || '').split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) {
                frag.appendChild(document.createTextNode(part));
                return;
              }
              const w = document.createElement('span');
              w.className = 'w';
              w.textContent = part;
              frag.appendChild(w);
            });
            child.replaceWith(frag);
          } else if (child.nodeType === 1) {
            wrap(child);
          }
        });
      };
      wrap(el);
      return {
        section: (el.closest('section') || el) as HTMLElement,
        words: Array.from(el.querySelectorAll<HTMLElement>('.w')),
      };
    });

    const light = () => {
      const h = window.innerHeight;
      manifestos.forEach((m) => {
        const r = m.section.getBoundingClientRect();
        if (r.bottom < 0 || r.top > h) return;
        const p = Math.min(1, Math.max(0, (h * 0.8 - r.top) / (r.height + h * 0.45)));
        const n = m.words.length;
        m.words.forEach((w, i) => {
          w.style.setProperty('--lit', p * (n + 1) > i + 1 ? '1' : '0');
        });
      });
    };
    const onScrollLight = () => {
      window.requestAnimationFrame(light);
    };

    document.addEventListener('load', onSoftLoad, true);
    document.addEventListener('loadedmetadata', onSoftLoad, true);
    window.addEventListener('resize', softenAll);
    window.addEventListener('resize', paint);
    window.addEventListener('scroll', onScrollPaint, { passive: true });
    window.addEventListener('scroll', onScrollLight, { passive: true });
    softenAll();
    paint();
    light();

    let cleanupRaf = 0;
    cleanupRaf = window.requestAnimationFrame(() => softenAll());

    return () => {
      window.cancelAnimationFrame(cleanupRaf);
      window.cancelAnimationFrame(raf);
      window.cancelAnimationFrame(praf);
      runRaf(false);
      near.disconnect();
      far.disconnect();
      run.disconnect();
      io.disconnect();
      chapters.forEach((c) => detach(c));
      stages.forEach((s) => {
        s.removeEventListener('pointerover', pointerOver);
        s.removeEventListener('pointerout', pointerOut);
      });
      window.removeEventListener('scroll', onFirstScroll);
      window.removeEventListener('scroll', onScrollPaint);
      window.removeEventListener('scroll', onScrollLight);
      window.removeEventListener('resize', softenAll);
      window.removeEventListener('resize', paint);
      document.removeEventListener('load', onSoftLoad, true);
      document.removeEventListener('loadedmetadata', onSoftLoad, true);
      removePointer();
      document.documentElement.style.removeProperty('--cue');
      document.documentElement.style.removeProperty('--mx');
      document.documentElement.style.removeProperty('--my');
    };
  }, []);

  return (
    <section
      className="chapter"
      id="top"
      data-video={VIDEO_URL}
      data-duration={DURATION}
      data-poster={POSTER_URL}
      data-label="The Reve Foundation"
    >
      <div className="stage">
        <img
          className="plate"
          src={POSTER_URL}
          width="1920"
          height="1080"
          fetchPriority="high"
          decoding="async"
          alt="A quiet sage-green writing room: morning sun falls across a pale floor toward a light oak desk beneath a tall ivy-framed window."
        />
        <div className="veil" aria-hidden="true" />
        <div className="veil veil-deep" aria-hidden="true" />
        <div className="veil veil-light" aria-hidden="true" />

        <div
          className="beat"
          data-from="0"
          data-to="0.2143"
          data-pos="bottom"
          data-depth="soft"
          data-tone="light"
          data-motion="track"
          id="beat-brand"
        >
          <p className="stamp">The Reve Foundation · Palo Alto, California</p>
          <h1 data-split="lines">
            Philanthropy with
            <br />
            <em className="accent">purpose.</em>
          </h1>
          <p className="sub">A private charitable foundation, held quietly and thoughtfully.</p>
        </div>

        <div
          className="beat"
          data-from="0.2143"
          data-to="0.3571"
          data-pos="left"
          data-depth="soft"
          data-tone="light"
          data-motion="drift"
          id="beat-promise"
        >
          <p className="stamp">01 — A promise</p>
          <h2 data-split="lines">
            Care, given
            <br />
            <em className="accent">deliberately.</em>
          </h2>
        </div>

        <div
          className="beat"
          data-from="0.3571"
          data-to="0.5714"
          data-pos="left"
          data-depth="soft"
          data-tone="dark"
          data-motion="rise"
          id="beat-mission"
        >
          <p className="stamp">02 — Our mission</p>
          <h2 data-split="lines">
            Building a better future
            <br />
            through thoughtful
            <br />
            <em className="accent">philanthropy.</em>
          </h2>
          <p className="sub">Opportunities for individuals and communities to thrive.</p>
          <a className="cta" href="#mission">
            Explore our mission
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </a>
        </div>

        <div
          className="beat"
          data-from="0.5714"
          data-to="0.7143"
          data-pos="left"
          data-depth="soft"
          data-tone="dark"
          data-motion="zoom"
          id="beat-approach"
        >
          <p className="stamp">03 — Our approach</p>
          <h2 data-split="lines">
            Thoughtful giving.
            <br />
            <em className="accent">Meaningful impact.</em>
          </h2>
        </div>

        <div
          className="beat"
          data-from="0.7143"
          data-to="1"
          data-pos="left"
          data-depth="soft"
          data-tone="dark"
          data-motion="rise"
          id="beat-contact"
        >
          <p className="stamp">04 — Contact</p>
          <h2 data-split="lines">
            Connect with
            <br />
            the <em className="accent">foundation.</em>
          </h2>
          <a className="cta" href="#contact">
            Get in touch
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </a>
          <ul className="chips" data-side="right">
            <li>Palo Alto, California</li>
            <li>Sherri Davis</li>
          </ul>
        </div>

        <div className="scroll-cue" aria-hidden="true">
          <span />
        </div>
      </div>
    </section>
  );
}